import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLocale } from '../context/LocaleContext';
import { resolveProductSportSlug } from '../lib/productSportCache';
import { isSupabaseConfigured } from '../lib/supabaseClient';
import { CURRENCIES, REGIONS } from '../utils/locale';
import { SPORTS } from '../utils/sports';
import LeagueTeamNav from './LeagueTeamNav';
import SearchOverlay from './SearchOverlay';
import SportsNav from './SportsNav';
import BrandLogo from './BrandLogo';
import './TopBar.css';

function IconMenu() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function IconBag() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconUser() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function TopBar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [productSportSlug, setProductSportSlug] = useState('');
  const [overDark, setOverDark] = useState(false);
  const wrapRef = useRef(null);
  const drawerRef = useRef(null);
  const { totalCount } = useCart();
  const { user, signOut } = useAuth();
  const { region, currency, setRegion, setCurrency } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === '/';
  const isSports = location.pathname.startsWith('/sports/');
  const isProduct = location.pathname.startsWith('/product/');
  const isShop = location.pathname.startsWith('/shop');

  const routeSportSlug = location.pathname.match(/^\/sports\/([^/]+)/)?.[1] || '';
  const productId = location.pathname.match(/^\/product\/([^/]+)/)?.[1] || '';

  const activeSportSlug = isSports
    ? routeSportSlug
    : isProduct
      ? productSportSlug
      : '';

  const showSportsList = isHome || (isShop && !isSports);
  const showTeamsList = Boolean(activeSportSlug) && (isSports || isProduct);
  const inverseChrome = overDark;

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  // White chrome over dark surfaces, ink over light — follows what sits under the bar.
  useEffect(() => {
    let frame = 0;

    const syncChrome = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const probeY = Math.max(24, (wrapRef.current?.offsetHeight || 72) * 0.55);
        const darkSurfaces = document.querySelectorAll('[data-topbar-surface="dark"]');
        let dark = false;

        darkSurfaces.forEach(surface => {
          const rect = surface.getBoundingClientRect();
          if (rect.top <= probeY && rect.bottom > probeY) {
            dark = true;
          }
        });

        setOverDark(dark);
      });
    };

    syncChrome();
    // Re-measure after route paint / images
    const settle = window.setTimeout(syncChrome, 80);
    const settleLate = window.setTimeout(syncChrome, 320);

    window.addEventListener('scroll', syncChrome, { passive: true });
    window.addEventListener('resize', syncChrome);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      window.clearTimeout(settleLate);
      window.removeEventListener('scroll', syncChrome);
      window.removeEventListener('resize', syncChrome);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKey = event => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);

    const focusable = drawerRef.current?.querySelector(
      'button, a, select, input, [tabindex]:not([tabindex="-1"])'
    );
    focusable?.focus();

    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;

    const syncOffset = () => {
      const height = Math.ceil(node.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--topbar-offset', `${height}px`);
    };

    syncOffset();
    const observer = new ResizeObserver(syncOffset);
    observer.observe(node);
    window.addEventListener('resize', syncOffset);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncOffset);
    };
  }, [isHome, showSportsList, showTeamsList, activeSportSlug, location.pathname]);

  useEffect(() => {
    if (!isProduct || !productId || !isSupabaseConfigured) {
      setProductSportSlug('');
      return undefined;
    }

    let cancelled = false;

    resolveProductSportSlug(productId).then(slug => {
      if (!cancelled) setProductSportSlug(slug);
    });

    return () => {
      cancelled = true;
    };
  }, [isProduct, productId]);

  const handleSignOut = async () => {
    await signOut();
    setMenuOpen(false);
    navigate('/');
  };

  return (
    <>
      <div
        ref={wrapRef}
        className={`topbar-wrap${inverseChrome ? ' topbar-wrap--inverse' : ''}`}
      >
        <header className="topbar">
          <div className="topbar-left">
            <button
              type="button"
              className="topbar-icon-btn"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
            >
              <IconMenu />
            </button>
            <Link to="/" className="topbar-logo" aria-label="Laurenti Apparel home">
              <BrandLogo />
            </Link>
          </div>

          {showSportsList && (
            <div className="topbar-center topbar-center--wide">
              <SportsNav variant="editorial" />
            </div>
          )}

          {showTeamsList && (
            <div className="topbar-center topbar-center--wide">
              <LeagueTeamNav sportSlug={activeSportSlug} />
            </div>
          )}

          <div className="topbar-actions">
            <Link to="/cart" className="topbar-icon-btn" aria-label="Cart">
              <IconBag />
              {totalCount > 0 && (
                <span className="topbar-cart-badge">{totalCount}</span>
              )}
            </Link>
            <button
              type="button"
              className="topbar-icon-btn"
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
            >
              <IconSearch />
            </button>
            <Link
              to={user ? '/orders' : '/login'}
              className="topbar-icon-btn"
              aria-label={user ? 'Account' : 'Sign in'}
            >
              <IconUser />
            </Link>
          </div>
        </header>
      </div>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      {menuOpen && (
        <>
          <div
            className="topbar-menu-overlay"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div
            ref={drawerRef}
            className="topbar-menu-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <button
              type="button"
              className="topbar-menu-close"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
            >
              ×
            </button>
            <BrandLogo className="topbar-menu-logo" />

            <p className="topbar-menu-section">Navigate</p>
            <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
            <Link to="/shop" onClick={() => setMenuOpen(false)}>Shop</Link>
            <Link to="/cart" onClick={() => setMenuOpen(false)}>Bag</Link>
            {user ? (
              <>
                <Link to="/orders" onClick={() => setMenuOpen(false)}>Orders</Link>
                <button type="button" className="topbar-menu-link" onClick={handleSignOut}>
                  Sign Out
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMenuOpen(false)}>Sign In</Link>
            )}

            <p className="topbar-menu-section" style={{ marginTop: 28 }}>Sports</p>
            {SPORTS.map(sport => (
              <Link
                key={sport.slug}
                to={`/sports/${sport.slug}`}
                onClick={() => setMenuOpen(false)}
              >
                {sport.name}
              </Link>
            ))}

            <p className="topbar-menu-section" style={{ marginTop: 28 }}>House</p>
            <Link to="/shipping" onClick={() => setMenuOpen(false)}>Shipping</Link>
            <Link to="/returns" onClick={() => setMenuOpen(false)}>Returns</Link>
            <Link to="/privacy" onClick={() => setMenuOpen(false)}>Privacy</Link>
            <Link to="/terms" onClick={() => setMenuOpen(false)}>Terms</Link>

            <div className="topbar-locale">
              <p className="topbar-locale-label">Region</p>
              <select
                className="topbar-locale-select"
                value={region}
                onChange={event => setRegion(event.target.value)}
              >
                {REGIONS.map(item => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
              </select>

              <p className="topbar-locale-label">Currency</p>
              <select
                className="topbar-locale-select"
                value={currency}
                onChange={event => setCurrency(event.target.value)}
              >
                {CURRENCIES.map(item => (
                  <option key={item.code} value={item.code}>
                    {item.code} ({item.symbol}) — {item.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default TopBar;
